import { useEffect, useState } from 'react';
import { Link as RouterLink, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/PhoneInTalk';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsNoneOutlined';
import DashboardIcon from '@mui/icons-material/GridViewOutlined';
import ReceiptIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import AppsIcon from '@mui/icons-material/AppsOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEventsOutlined';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswerOutlined';
import SwapIcon from '@mui/icons-material/SwapHorizOutlined';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  firstName,
  initial,
  type Language,
  type NotificationDto,
  type NotificationUnreadCount,
  type Paginated,
  type PropertyDetail,
} from '@pmc/shared';
import { formatDateTime } from '@pmc/shared';
import { useLanguage } from '../lib/i18n';
import { useSession } from '../lib/session';
import { api } from '../lib/api';
import { PMC } from '../theme';
import { UpdateContactDetailsDialog } from './UpdateContactDetailsDialog';
import { LogoutConfirmDialog } from './LogoutConfirmDialog';

/**
 * Application shell.
 *
 * Mirrors the legacy behaviour: a visitor who has not located a Property ID
 * gets the public site header; once a PID is located the shell switches to the
 * property-scoped layout with a sidebar, notification bell and owner chip.
 */

// Services is deliberately absent here — it's only meaningful for a
// property owner who has already located a PID (see PropertyShell's own
// nav below). A visitor without a PID applies via the "PT1 - New Taxation
// Service" link on the home page instead.
const NAV_LINKS = [
  { to: '/', key: 'nav_home', end: true },
  { to: '/calculator', key: 'nav_calculator' },
  { to: '/faqs', key: 'nav_faqs' },
  { to: '/about', key: 'nav_about' },
  { to: '/contact', key: 'nav_contact' },
] as const;

const SIDEBAR_WIDTH = 236;
const TOPBAR_HEIGHT = 34;

/**
 * Dark utility strip: support contacts and the language toggle.
 *
 * Sticky at the very top, above the brand/nav bar below it (which is sticky
 * at `top: TOPBAR_HEIGHT`, not `top: 0`) — without this, scrolling any page
 * even a little makes the whole strip scroll away since it isn't the sticky
 * element, leaving only the nav bar behind and reading as a missing header.
 */
function TopBar() {
  const { t, lang, setLang } = useLanguage();
  const { config } = useSession();

  const pill = (value: Language, label: string) => (
    <Box
      component="button"
      type="button"
      onClick={() => setLang(value)}
      sx={{
        border: 0,
        cursor: 'pointer',
        px: 1.4,
        py: 0.2,
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: lang === value ? 700 : 500,
        fontFamily: 'inherit',
        color: lang === value ? PMC.navy : '#fff',
        backgroundColor: lang === value ? '#fff' : 'transparent',
      }}
    >
      {label}
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1202,
        backgroundColor: PMC.navy,
        color: '#fff',
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        minHeight: TOPBAR_HEIGHT,
      }}
      className="no-print"
    >
      {/* The language toggle sits outside the maxWidth="lg" Container below,
          on its own, so it pins to the true edge of the viewport instead of
          just the edge of the centered content column. */}
      <Container maxWidth="lg" sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, opacity: 0.92, whiteSpace: 'nowrap' }}>
            {t('topbar_brand')}
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ display: { xs: 'none', md: 'flex' }, opacity: 0.92 }}
          >
            <Link
              href={`mailto:${config?.supportEmail ?? ''}`}
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12 }}
            >
              <EmailIcon sx={{ fontSize: 14 }} />
              {config?.supportEmail}
            </Link>
            <Box sx={{ opacity: 0.35 }}>|</Box>
            <Link
              href={`tel:${(config?.supportPhone ?? '').replace(/\s/g, '')}`}
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12 }}
            >
              <PhoneIcon sx={{ fontSize: 14 }} />
              {config?.supportPhone}
            </Link>
          </Stack>
        </Stack>
      </Container>

      <Stack
        direction="row"
        sx={{
          backgroundColor: 'rgba(255,255,255,.16)',
          borderRadius: 999,
          p: '2px',
          flexShrink: 0,
          mr: { xs: 1.5, sm: 3 },
        }}
      >
        {pill('mr', 'मराठी')}
        {pill('en', 'English')}
      </Stack>
    </Box>
  );
}

function Brand({ to = '/' }: { to?: string }) {
  const { t } = useLanguage();
  return (
    <Stack
      component={RouterLink}
      to={to}
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{ textDecoration: 'none', minWidth: 0 }}
    >
      <Box
        component="img"
        src="/PMC_Logo.png"
        alt=""
        sx={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 14.5, fontWeight: 700, color: PMC.primary, lineHeight: 1.2 }}
          noWrap
        >
          {t('org_name')}
        </Typography>
        <Typography sx={{ fontSize: 11, color: PMC.muted, lineHeight: 1.2 }} noWrap>
          {t('dept_name')}
        </Typography>
      </Box>
    </Stack>
  );
}

/** Public site header with the main navigation. */
function PublicHeader() {
  const { t } = useLanguage();
  const { session } = useSession();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // FAQ reached from the property sidebar (a property already located) is
  // meant to be read distraction-free — no nav links, no Search Property CTA
  // — since the sidebar's own Back button is the way out. A visitor with no
  // property located has no sidebar at all, so the full public nav stays;
  // otherwise they'd have no way to get anywhere else from this page.
  const minimal = location.pathname === '/faqs' && Boolean(session?.activePropertyId);

  const links = NAV_LINKS.map((link) => (
    <Box
      key={link.to}
      component={NavLink}
      to={link.to}
      end={'end' in link ? link.end : false}
      onClick={() => setDrawerOpen(false)}
      sx={{
        textDecoration: 'none',
        fontSize: 13.5,
        fontWeight: 500,
        color: PMC.ink,
        py: 0.5,
        borderBottom: '2px solid transparent',
        whiteSpace: 'nowrap',
        '&.active': { color: '#3B42C4', fontWeight: 600, borderBottomColor: '#3B42C4' },
        '&:hover': { color: '#3B42C4' },
      }}
    >
      {t(link.key)}
    </Box>
  ));

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="inherit"
      className="no-print"
      sx={{ top: TOPBAR_HEIGHT, borderBottom: `1px solid ${PMC.line}`, backgroundColor: '#fff' }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: 60, gap: 2 }}>
          <Brand />
          <Box sx={{ flex: 1 }} />
          {minimal ? null : (
            <>
              <Stack direction="row" spacing={2.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
                {links}
              </Stack>
              <Button
                component={RouterLink}
                to="/search"
                variant="contained"
                size="small"
                startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  borderRadius: '24px',
                  px: 2.5,
                  py: 1,
                  backgroundColor: '#3B42C4',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#3036A8',
                    boxShadow: '0 2px 8px rgba(59, 66, 196, 0.25)',
                  },
                }}
              >
                {t('search_title')}
              </Button>
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                aria-label="Open menu"
              >
                <MenuIcon />
              </IconButton>
            </>
          )}
        </Toolbar>
      </Container>

      {minimal ? null : (
        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Stack spacing={2.5} sx={{ width: 240, p: 3 }}>
            {links}
            <Button component={RouterLink} to="/search" variant="contained" fullWidth>
              {t('search_title')}
            </Button>
          </Stack>
        </Drawer>
      )}
    </AppBar>
  );
}

/** Read state for the live-derived "bill generated" feed lives client-side
 *  only — see domain/notifications.ts on the API side for why. */
function lastSeenBillsAt(propertyId: number): string {
  return localStorage.getItem(`notif_seen_${propertyId}`) ?? '';
}
function markBillsSeen(propertyId: number): void {
  localStorage.setItem(`notif_seen_${propertyId}`, new Date().toISOString());
}

/** Notification bell: recent persisted notifications + the live bill feed. */
function ActivityBell({ propertyId }: { propertyId: number }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const { data: unread } = useQuery({
    queryKey: ['notifications-unread', propertyId],
    queryFn: () =>
      api.get<NotificationUnreadCount>(`/properties/${propertyId}/notifications/unread-count`),
    staleTime: 60_000,
  });

  const { data: recent } = useQuery({
    queryKey: ['notifications', propertyId, 'recent'],
    queryFn: () =>
      api.get<Paginated<NotificationDto>>(`/properties/${propertyId}/notifications`, {
        page: 1,
        perPage: 5,
      }),
    staleTime: 60_000,
  });

  const items = recent?.rows ?? [];
  const derivedUnread = items.filter(
    (n) => n.isDerived && n.createdAt > lastSeenBillsAt(propertyId),
  ).length;
  const badgeCount = (unread?.unread ?? 0) + derivedUnread;

  const markAllRead = useMutation({
    mutationFn: () => api.post(`/properties/${propertyId}/notifications/read-all`),
    onSuccess: () => {
      markBillsSeen(propertyId);
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread', propertyId] });
      void queryClient.invalidateQueries({ queryKey: ['notifications', propertyId] });
    },
  });

  return (
    <>
      <Tooltip title={t('notifications')}>
        <IconButton
          onClick={(e) => {
            setAnchor(e.currentTarget);
            if (badgeCount > 0) markAllRead.mutate();
          }}
          aria-label={t('notifications')}
        >
          <Badge badgeContent={badgeCount} color="primary" max={9}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 320, maxWidth: '92vw' } } }}
      >
        <Typography variant="h6" sx={{ px: 2, py: 1 }}>
          {t('notifications')}
        </Typography>
        <Divider />
        {items.length === 0 ? (
          <MenuItem disabled sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('no_notifications')}
            </Typography>
          </MenuItem>
        ) : (
          items.map((item) => (
            <MenuItem
              key={item.id}
              sx={{ display: 'block', py: 1.1 }}
              onClick={() => {
                setAnchor(null);
                if (item.linkPath) navigate(item.linkPath);
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.refId ? `${item.refId} · ` : ''}
                {formatDateTime(item.createdAt)}
              </Typography>
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate(`/property/${propertyId}/notifications`);
          }}
          sx={{ justifyContent: 'center', color: PMC.primary, fontWeight: 600 }}
        >
          {t('view_all_notifications')}
        </MenuItem>
      </Menu>
    </>
  );
}

/** Property-scoped shell: header with owner chip plus a sidebar. */
function PropertyShell({
  propertyId,
  children,
}: {
  propertyId: number;
  children: React.ReactNode;
}) {
  const { t } = useLanguage();
  const { session, clearSession, refresh } = useSession();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [confirmDialogMode, setConfirmDialogMode] = useState<'logout' | 'change_property' | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleConfirmAction = async () => {
    const target = confirmDialogMode === 'change_property' ? '/search' : '/';
    await clearSession();
    navigate(target, { replace: true });
  };

  // Same query key BillViewPage/PropertyDashboardPage already use for this
  // property, so this rides their cache instead of firing an extra request.
  const { data: property } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => api.get<PropertyDetail>(`/properties/${propertyId}`),
  });
  const primaryOwnerName =
    property?.owners.find((o) => o.isPrimaryOwner)?.ownerName ?? property?.owners[0]?.ownerName ?? '';
  // Read off this same fetch (not the session) so it always matches propertyId.
  const propertyNo = property?.propertyNo ?? '';

  // This request is also what establishes activePropertyId in the server-side
  // session cookie (see properties.routes.ts). A visitor who reaches a
  // property page any way other than Search/Home — a bookmark, a direct URL,
  // browser back/forward — never triggers one of those pages' explicit
  // refresh() calls, so the client's session state can lag the cookie
  // indefinitely (nothing else invalidates it). That leaves anything reading
  // session.activePropertyId off the SPA's cached session — e.g. the FAQ
  // page's Back button, reached from here via a plain `/faqs` link with no
  // property id of its own to fall back on — treating a real active property
  // as if none were located. Re-syncing once this fetch confirms the cookie
  // is actually set fixes that at the source instead of in each consumer.
  useEffect(() => {
    if (property && session?.activePropertyId !== propertyId) refresh();
  }, [property, propertyId, session?.activePropertyId, refresh]);

  const items = [
    { to: `/property/${propertyId}`, key: 'nav_dashboard', icon: <DashboardIcon fontSize="small" /> },
    { to: `/property/${propertyId}/bills`, key: 'view_bills', icon: <PaymentsIcon fontSize="small" /> },
    { to: `/property/${propertyId}/history`, key: 'view_history', icon: <ReceiptIcon fontSize="small" /> },
    { to: `/property/${propertyId}/services`, key: 'nav_services', icon: <AppsIcon fontSize="small" /> },
    // { to: `/property/${propertyId}/schemes`, key: 'tab_schemes', icon: <EmojiEventsIcon fontSize="small" /> },
    { to: `/property/${propertyId}/queries`, key: 'nav_myqueries', icon: <QuestionAnswerIcon fontSize="small" /> },
    { to: `/property/${propertyId}/notifications`, key: 'nav_notifications', icon: <NotificationsIcon fontSize="small" /> },
    { to: '/faqs', key: 'nav_faq', icon: <HelpIcon fontSize="small" /> },
  ];

  const nav = (
    <List sx={{ px: 1.25, py: 2 }}>
      {items.map((item) => {
        const selected =
          item.to === `/property/${propertyId}`
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to);
        return (
          <ListItemButton
            key={item.to}
            component={RouterLink}
            to={item.to}
            selected={selected}
            onClick={() => setMobileOpen(false)}
            sx={{
              borderRadius: 2,
              mb: 0.25,
              '&.Mui-selected': {
                backgroundColor: PMC.primaryLight,
                color: PMC.primary,
                '& .MuiListItemIcon-root': { color: PMC.primary },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={t(item.key)}
              slotProps={{ primary: { fontSize: 13.5, fontWeight: selected ? 600 : 500 } }}
            />
          </ListItemButton>
        );
      })}
    </List>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: `calc(100vh - ${TOPBAR_HEIGHT}px)` }}>
      <AppBar
        position="sticky"
        elevation={0}
        color="inherit"
        className="no-print"
        sx={{ top: TOPBAR_HEIGHT, borderBottom: `1px solid ${PMC.line}`, backgroundColor: '#fff', zIndex: 1201 }}
      >
        <Toolbar sx={{ minHeight: 60, gap: 1.5 }}>
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
          <Brand to={`/property/${propertyId}`} />
          <Box sx={{ flex: 1 }} />
          <ActivityBell propertyId={propertyId} />
          <Stack
            component="button"
            type="button"
            onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ border: 0, background: 'none', cursor: 'pointer', p: 0.5, borderRadius: 2, '&:hover': { backgroundColor: PMC.bgSoft } }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: PMC.primary, fontSize: 13 }}>
              {primaryOwnerName ? initial(primaryOwnerName) : initial(propertyNo)}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left' }}>
              <Typography sx={{ fontSize: 11, color: PMC.muted, lineHeight: 1.2 }}>{t('welcome')}</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>
                {primaryOwnerName || firstName(propertyNo) || propertyNo}
              </Typography>
            </Box>
          </Stack>
          <Menu
            anchorEl={accountMenuAnchor}
            open={Boolean(accountMenuAnchor)}
            onClose={() => setAccountMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setAccountMenuAnchor(null);
                setContactDialogOpen(true);
              }}
            >
              <ListItemIcon>
                <ContactMailOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('update_contact_details')} />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAccountMenuAnchor(null);
                setConfirmDialogMode('logout');
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon sx={{ color: 'error.main' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('nav_logout')} />
            </MenuItem>
          </Menu>
          <Button
            onClick={() => setConfirmDialogMode('change_property')}
            size="small"
            variant="outlined"
            startIcon={<SwapIcon />}
            sx={{ ml: 1, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {t('change_property')}
          </Button>
          <Button
            onClick={() => setConfirmDialogMode('logout')}
            size="small"
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            sx={{ ml: 1, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {t('nav_logout')}
          </Button>
        </Toolbar>
      </AppBar>

      <UpdateContactDetailsDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        propertyId={propertyId}
        currentMobile={property?.mobileNo ?? null}
        currentEmail={property?.emailId ?? null}
      />

      <LogoutConfirmDialog
        open={confirmDialogMode !== null}
        mode={confirmDialogMode ?? 'logout'}
        onClose={() => setConfirmDialogMode(null)}
        onConfirm={handleConfirmAction}
        propertyNo={propertyNo}
        ownerName={primaryOwnerName}
      />

      <Box sx={{ display: 'flex', flex: 1 }}>
        {isDesktop ? (
          <Box
            component="nav"
            className="no-print"
            sx={{
              width: SIDEBAR_WIDTH,
              flexShrink: 0,
              borderRight: `1px solid ${PMC.line}`,
              backgroundColor: '#fff',
              position: 'sticky',
              top: `calc(${TOPBAR_HEIGHT}px + 60px)`,
              height: `calc(100vh - ${TOPBAR_HEIGHT}px - 60px)`,
              overflowY: 'auto',
            }}
          >
            {nav}
          </Box>
        ) : (
          <Drawer
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            sx={{
              zIndex: (theme) => theme.zIndex.modal + 1,
              '& .MuiDrawer-paper': {
                width: 270,
                boxSizing: 'border-box',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Drawer Top Header with Logo and Close button */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: `1px solid ${PMC.line}`,
                }}
              >
                <Box onClick={() => setMobileOpen(false)} sx={{ cursor: 'pointer' }}>
                  <Brand to={`/property/${propertyId}`} />
                </Box>
                <IconButton
                  onClick={() => setMobileOpen(false)}
                  size="small"
                  aria-label="Close menu"
                  sx={{ color: '#64748B', '&:hover': { backgroundColor: '#F1F5F9' } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Scrollable Nav Items */}
              <Box sx={{ flex: 1, overflowY: 'auto' }}>{nav}</Box>

              <Divider />
              <Box sx={{ p: 1.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<LogoutIcon />}
                  onClick={() => {
                    setMobileOpen(false);
                    setConfirmDialogMode('logout');
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  {t('nav_logout')}
                </Button>
              </Box>
            </Box>
          </Drawer>
        )}

        <Box component="main" sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
          {children}
        </Box>
      </Box>

      <StandardFooter />
    </Box>
  );
}

function timestamp(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${min}`;
}

/** Standard footer bar — copyright left, security/session status right. Used on every page, logged-in or not. */
function StandardFooter() {
  const { t } = useLanguage();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <Box
      component="footer"
      className="no-print"
      sx={{ borderTop: `1px solid ${PMC.line}`, backgroundColor: '#fff', px: { xs: 2, md: 3 }, py: 1.25 }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography sx={{ fontSize: 12, color: PMC.muted }}>
          © {now.getFullYear()} {t('footer_system_name')}. {t('footer_rights')}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <VerifiedUserOutlinedIcon sx={{ fontSize: 14, color: PMC.muted }} />
          <Typography sx={{ fontSize: 12, color: PMC.muted }}>{t('footer_secure_system')}</Typography>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: PMC.muted }} />
          <Typography sx={{ fontSize: 12, color: PMC.muted }} className="num">
            {timestamp(now)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

const FOOTER_LINKS = [
  { to: '/', key: 'nav_home' },
  { to: '/calculator', key: 'nav_calculator' },
  { to: '/about', key: 'nav_about' },
  { to: '/faqs', key: 'nav_faqs' },
  { to: '/contact', key: 'nav_contact' },
] as const;

function Footer() {
  const { t } = useLanguage();
  const { config } = useSession();

  return (
    <Box
      component="footer"
      className="no-print"
      sx={{ backgroundColor: PMC.navy, color: 'rgba(255,255,255,.75)' }}
    >
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 4, md: 6 }}
          sx={{ '& > *': { flex: 1 } }}
        >
          <Box>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
              <Box
                component="img"
                src="/PMC_Logo.png"
                alt=""
                sx={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                  {t('org_name')}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                  {t('dept_name')}
                </Typography>
              </Box>
            </Stack>
            <Typography variant="body2">{t('hero_tagline')}</Typography>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 700, color: '#fff', mb: 1.5, fontSize: 13.5 }}>
              {t('quick_links')}
            </Typography>
            <Stack spacing={1}>
              {FOOTER_LINKS.map((link) => (
                <Link
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  color="inherit"
                  variant="body2"
                  underline="hover"
                >
                  {t(link.key)}
                </Link>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 700, color: '#fff', mb: 1.5, fontSize: 13.5 }}>
              {t('get_in_touch')}
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                {t('helpline')}: {config?.supportPhone}
              </Typography>
              <Typography variant="body2">
                {t('email_us')}: {config?.supportEmail}
              </Typography>
              <Typography variant="body2">
                {t('office')}: {t('office_addr')}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

export function Layout() {
  const { session } = useSession();
  const location = useLocation();

  // Scroll to top of page on every route navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Public pages keep the public header even once a PID is located, so the
  // shell does not flip while the visitor is reading About or the FAQs.
  const isPropertyRoute =
    location.pathname.startsWith('/property/') ||
    location.pathname.startsWith('/pay/') ||
    location.pathname.startsWith('/receipt/') ||
    location.pathname.startsWith('/services') ||
    location.pathname.startsWith('/applications');

  // The URL is the source of truth once it names a property: session.
  // activePropertyId is only a fallback for checkout/receipt routes, which
  // carry a billId/txnNo/receiptId instead of a propertyId. Reading from the
  // route avoids the header showing the *previous* property while the
  // session cookie (updated only after the new property's own fetch
  // resolves) catches up.
  const routePropertyId = location.pathname.match(/^\/property\/(\d+)/)?.[1];
  const propertyId = routePropertyId ? Number(routePropertyId) : (session?.activePropertyId ?? null);

  // PT1 is for new properties not yet assessed for tax (no PID exists yet).
  // When accessed via the public link (/services/... without /property/:id),
  // it must never bind to or render inside a previous property's shell.
  const isStandAlonePt1 = !routePropertyId && location.pathname.includes('PT1');

  if (propertyId && isPropertyRoute && !isStandAlonePt1) {
    return (
      <>
        <TopBar />
        <PropertyShell propertyId={propertyId}>
          <Outlet />
        </PropertyShell>
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <TopBar />
      <PublicHeader />
      <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </Box>
      <Footer />
      <StandardFooter />
    </Box>
  );
}
